import React from "react";
import styled from "styled-components";
import { theme } from "../../../../utils/theme";
import { FlexDiv, SkeletonBlock } from "../../../UI";

export const EmailSettingsTabSkeleton: React.FC = () => {
  return (
    <Body>
      {/* Header skeleton */}
      <HeaderContainer>
        <SkeletonBlock width={150} height={32} borderRadius={4} />
        <ButtonsContainer>
          <SkeletonBlock width={80} height={36} borderRadius={4} delayNumber={1} />
          <SkeletonBlock width={80} height={36} borderRadius={4} delayNumber={2} />
        </ButtonsContainer>
      </HeaderContainer>

      <Page>
        {/* Rep Email Address Format Section */}
        <SectionContainer>
          <FlexDiv direction="column" gap={16}>
            {/* Title and description skeleton */}
            <SkeletonBlock width={220} height={28} borderRadius={4} delayNumber={3} />
            <SkeletonBlock width={400} height={18} borderRadius={4} delayNumber={4} />
          </FlexDiv>

          <FlexDiv gap={40} align="center" style={{ marginTop: "24px" }}>
            {/* Email input & domain */}
            <InputDomainContainer>
              <SkeletonBlock width={375} height={48} borderRadius={4} delayNumber={5} />
              <SkeletonBlock width={120} height={24} borderRadius={4} delayNumber={6} />
            </InputDomainContainer>

            {/* Rep tokens skeleton */}
            <TokensSkeleton delayStart={7} />
          </FlexDiv>
        </SectionContainer>

        {/* Email Settings Section */}
        <SectionContainer>
          <FlexDiv direction="column" gap={16}>
            {/* Title and description skeleton */}
            <SkeletonBlock width={180} height={28} borderRadius={4} delayNumber={15} />
            <SkeletonBlock width={400} height={18} borderRadius={4} delayNumber={16} />
          </FlexDiv>

          <FlexDiv gap={40} style={{ marginTop: "24px" }}>
            {/* Template editor skeleton */}
            <EditorSkeleton />

            {/* Tokens container skeleton with search */}
            <TokensSkeletonWithSearch />
          </FlexDiv>
        </SectionContainer>
      </Page>
    </Body>
  );
};

const TokensSkeletonWithSearch = () => {
  return (
    <TokensSkeletonContainer>
      <TokensSkeletonHeader>
        {/* Title */}
        <SkeletonBlock width={60} height={20} borderRadius={4} delayNumber={25} />
        {/* Description */}
        <SkeletonBlock width={250} height={16} borderRadius={4} delayNumber={26} />
        {/* Search input */}
        <SkeletonBlock width="100%" height={38} borderRadius={4} delayNumber={27} />
      </TokensSkeletonHeader>

      <TokensSkeletonContent>
        {/* Tokens */}
        <TokenCategoriesList>
          {/* Category 1 */}
          <TokenCategoryGroup>
            <SkeletonBlock width={120} height={20} borderRadius={4} delayNumber={28} />
            <TokensRow>
              <SkeletonBlock width={100} height={36} borderRadius={8} delayNumber={29} />
              <SkeletonBlock width={120} height={36} borderRadius={8} delayNumber={30} />
              <SkeletonBlock width={110} height={36} borderRadius={8} delayNumber={31} />
            </TokensRow>
          </TokenCategoryGroup>

          {/* Category 2 */}
          <TokenCategoryGroup>
            <SkeletonBlock width={90} height={20} borderRadius={4} delayNumber={32} />
            <TokensRow>
              <SkeletonBlock width={120} height={36} borderRadius={8} delayNumber={33} />
              <SkeletonBlock width={90} height={36} borderRadius={8} delayNumber={34} />
              <SkeletonBlock width={130} height={36} borderRadius={8} delayNumber={35} />
            </TokensRow>
          </TokenCategoryGroup>
        </TokenCategoriesList>
      </TokensSkeletonContent>
    </TokensSkeletonContainer>
  );
};

const TokensSkeleton = ({ delayStart = 0 }) => {
  return (
    <TokensSkeletonContainer style={{ height: "240px" }}>
      <TokensSkeletonHeader>
        {/* Title */}
        <SkeletonBlock width={60} height={20} borderRadius={4} delayNumber={delayStart} />
      </TokensSkeletonHeader>

      <TokensSkeletonContent>
        {/* Tokens */}
        <TokenCategoriesList>
          {/* Category */}
          <TokenCategoryGroup>
            <SkeletonBlock width={90} height={20} borderRadius={4} delayNumber={delayStart + 1} />
            <TokensRow>
              <SkeletonBlock width={100} height={36} borderRadius={8} delayNumber={delayStart + 2} />
              <SkeletonBlock width={120} height={36} borderRadius={8} delayNumber={delayStart + 3} />
              <SkeletonBlock width={90} height={36} borderRadius={8} delayNumber={delayStart + 4} />
            </TokensRow>
          </TokenCategoryGroup>
        </TokenCategoriesList>
      </TokensSkeletonContent>
    </TokensSkeletonContainer>
  );
};

const EditorSkeleton: React.FC = () => {
  return (
    <EditorContainer>
      {/* Toolbar skeleton */}
      <ToolbarSkeleton>
        <FlexDiv gap={8}>
          {[...Array(6)].map((_, index) => (
            <SkeletonBlock
              key={`toolbar-item-${index}`}
              width={24}
              height={24}
              borderRadius={4}
              delayNumber={17 + index}
            />
          ))}
        </FlexDiv>
      </ToolbarSkeleton>

      {/* Editor content skeleton */}
      <EditorContentSkeleton>
        <SkeletonBlock width="90%" height={16} borderRadius={4} delayNumber={21} />
        <SkeletonBlock width="70%" height={16} borderRadius={4} delayNumber={22} />
        <SkeletonBlock width="85%" height={16} borderRadius={4} delayNumber={23} />
        <SkeletonBlock width="40%" height={16} borderRadius={4} delayNumber={24} />
      </EditorContentSkeleton>
    </EditorContainer>
  );
};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Body = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 24px 40px 24px;
  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 8px;
`;

const EditorContainer = styled.div`
  width: 520px;
  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 8px;
  overflow: hidden;
`;

const ToolbarSkeleton = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${theme.border.neutral.secondary};
  background-color: ${theme.NEUTRAL100};
`;

const EditorContentSkeleton = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 200px;
`;

const TokensSkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 280px;
  height: 252px;
  overflow: hidden;
`;

const TokensSkeletonHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;
`;

const TokensSkeletonContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
`;

const TokenCategoriesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const TokenCategoryGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TokensRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const InputDomainContainer = styled(FlexDiv)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 4px;
  min-width: 350px;
`;
